import type { BlockContent, InlineRun, TocBehavior } from './content'

/**
 * What a book contains, computed from the book.
 *
 * A contents page is presentation derived from the book, never authored
 * beside it — the same rule the note numbering rests on, and for the same
 * reason: two places holding one fact are two places free to disagree. So this
 * is the ONE answer to "what is in the contents", and the screen rail, the
 * editor and the printed page all ask it rather than each deciding.
 */

export type DivisionRole =
  | 'front_matter'
  | 'half_title'
  | 'copyright'
  | 'dedication'
  | 'epigraph'
  | 'closing'
  | 'preface'
  | 'intro'
  | 'chapter'
  | 'appendix'
  | 'addendum'
  | 'notes'

/**
 * The pages a book has besides its text, in the order they are bound.
 *
 * The order is the model's rather than each book's, because it is a printing
 * convention every trade book keeps: a dedication before an epigraph, both
 * before the contents. A book that stated its own would be authoring
 * presentation, which is the one thing this model computes.
 *
 * A page the book does not have is simply absent from the list, so nothing
 * reserves a leaf, prints a heading or holds a place for it — the sequence is
 * over the pages that exist rather than over a fixed set of slots.
 *
 * `front_matter` is the page nobody has named, so it sorts after the named ones
 * and before the closing leaf, which is back matter and prints after the text.
 */
export const MATTER_ROLES = [
  'half_title',
  'copyright',
  'dedication',
  'epigraph',
  'front_matter',
  'closing',
] as const

export type MatterRole = (typeof MATTER_ROLES)[number]

/** Whether a role names a page of matter rather than a part of the text. */
export const isMatterRole = (
  role: DivisionRole | null | undefined,
): role is MatterRole =>
  !!role && (MATTER_ROLES as readonly string[]).includes(role)

/** Where a page of matter falls among the others. Unnamed pages sort last. */
export function matterOrder(role: DivisionRole | null | undefined): number {
  const at = role ? (MATTER_ROLES as readonly string[]).indexOf(role) : -1
  return at === -1 ? MATTER_ROLES.length : at
}

export type ContentsEntry = {
  /** The section or block the entry points at, and what it is found by. */
  id: string
  title: InlineRun
  /**
   * What the book CALLS this part — "Chapter Three", "Introduction".
   *
   * Computed here and stored nowhere. The number is the part's place among the
   * parts of its own kind, so moving a chapter renumbers the ones after it with
   * nothing edited, and the screen and the printed page cannot come to
   * different answers about which chapter this is.
   */
  label?: string
  role?: DivisionRole
  /**
   * Nesting in the CONTENTS, which is not the section's depth in the book.
   * A run-in heading sits one level under the section that holds it, and that
   * section may itself have been lifted when an ancestor was cut.
   */
  level: number
  children: ContentsEntry[]
}

/**
 * How many levels the contents lists before it stops.
 *
 * A contents that goes all the way down stops being a way to reach a place and
 * becomes the book again at a smaller size.
 */
export const CONTENTS_LEVELS = 3

/**
 * How many levels a PRINTED contents lists: what the book calls a part, and
 * what divides it. Deeper than that is a list of every question the book asks,
 * which is the book's own argument rather than a way into it — and on a page it
 * wraps every second line, which is what makes a contents page something a
 * reader gives up on. The rail goes one level further, because a list a reader
 * scrolls beside the text costs nothing to carry it.
 */
export const PRINTED_CONTENTS_LEVELS = 2

/**
 * Whether a run-in heading reaches the contents.
 *
 * The three values say what an author means, not how deep something sits:
 * `never` is a keyword inside prose, which is presentation; `normal` is a real
 * part, listed as long as the contents is still listing at that level; `always`
 * is a part worth reaching however deep it lies, and is the only thing that
 * outranks the level cut.
 */
export function inContents(
  behavior: TocBehavior,
  level: number,
  levels: number = CONTENTS_LEVELS,
): boolean {
  if (behavior === 'never') return false
  if (behavior === 'always') return true
  return level < levels
}

/** The shape `contentsOf` walks — a section as any surface holds one. */
export type SectionLike = {
  id: string
  title: InlineRun
  kind?: string
  role?: DivisionRole | null
  blocks: readonly { id: string; content: BlockContent }[]
  children: readonly SectionLike[]
}

/**
 * What each role is called, and whether it counts.
 *
 * A role that counts takes its place in a series of its own: the chapters are
 * numbered among the chapters and an appendix among the appendices, so an
 * introduction sitting between them shifts nothing. A role that does not count
 * is named and left at that — there is only ever one introduction, and "Notes
 * One" names nothing.
 *
 * The pages of matter are absent deliberately: none of them is listed. The
 * contents page lives IN the front matter, and a contents that lists itself,
 * the title page and the dedication is a list of the things a reader is already
 * holding.
 */
const ROLES: Record<
  Exclude<DivisionRole, MatterRole>,
  { name: string; counts: boolean }
> = {
  preface: { name: 'Preface', counts: false },
  intro: { name: 'Introduction', counts: false },
  chapter: { name: 'Chapter', counts: true },
  appendix: { name: 'Appendix', counts: true },
  addendum: { name: 'Addendum', counts: true },
  notes: { name: 'Notes', counts: false },
}

/**
 * A number as the word a book prints — "Chapter Three", never "Chapter 3".
 *
 * Words to twelve, which is past any chapter count this has met; beyond that
 * the figure is more readable than a compound word anyway, and a contents is
 * read rather than recited.
 */
const WORDS = [
  'One',
  'Two',
  'Three',
  'Four',
  'Five',
  'Six',
  'Seven',
  'Eight',
  'Nine',
  'Ten',
  'Eleven',
  'Twelve',
]

export function labelFor(role: DivisionRole, ordinal: number): string {
  if (isMatterRole(role)) return ''
  const spec = ROLES[role]
  if (!spec.counts) return spec.name
  return `${spec.name} ${WORDS[ordinal - 1] ?? ordinal}`
}

/**
 * The book's parts, nested, in reading order.
 *
 * A section beyond the cut is not listed, and its listable descendants are not
 * lost with it: they attach to the nearest ancestor that IS listed. Dropping
 * them would make `always` a promise the depth rule could quietly break, and
 * `always` exists precisely for the part that must be reachable.
 *
 * Front and back matter are left out entirely, and a top-level part is named by
 * its role and its place among the parts sharing that role — see `labelFor`. A
 * part whose role is unknown is still listed, without a name: it is in the book
 * whether or not anybody has said what it is, and dropping it would hide a
 * chapter because a field was blank.
 *
 * `notes` names a section of the book that no row in the tree holds: an
 * endnotes section is built from the note rows at render, so it is told to this
 * computation rather than appended to its answer somewhere else. Five pages of
 * endnotes nothing points at is exactly the part a reader goes to the contents
 * for. A book whose notes are set at the foot passes nothing, and a book that
 * holds a notes section of its own is already listed by the walk.
 */
export function contentsOf(
  sections: readonly SectionLike[],
  {
    levels = CONTENTS_LEVELS,
    notes,
  }: { levels?: number; notes?: { id: string; title: InlineRun } } = {},
): ContentsEntry[] {
  /* One running count per role, so each series is numbered among its own. */
  const counted = new Map<DivisionRole, number>()

  const walk = (list: readonly SectionLike[], level: number): ContentsEntry[] =>
    list.flatMap((s) => {
      /* A page of matter is not in the contents; nor is anything under it,
         since a contents listing the contents page's own parts is circular. */
      if (
        isMatterRole(s.role) ||
        s.kind === 'front_matter' ||
        s.kind === 'back_matter'
      )
        return []

      const headings = s.blocks
        .filter((b) => b.content.kind === 'run_in_heading')
        .filter((b) =>
          inContents(
            (b.content as Extract<BlockContent, { kind: 'run_in_heading' }>)
              .tocBehavior,
            level + 1,
            levels,
          ),
        )
        .map(
          (b): ContentsEntry => ({
            id: b.id,
            title: (
              b.content as Extract<BlockContent, { kind: 'run_in_heading' }>
            ).content,
            level: level + 1,
            children: [],
          }),
        )

      const below = [...headings, ...walk(s.children, level + 1)]

      /* Past the cut the section itself is not an entry, so whatever below it
         still qualifies is lifted to where the section would have been. */
      if (level >= levels) return below.map((e) => lift(e, level))

      /* Named only at the top level: a part inside a chapter is numbered within
         that chapter, which is a different series and the chapter's to state. */
      let label: string | undefined
      if (level === 0 && s.role) {
        const n = (counted.get(s.role) ?? 0) + 1
        counted.set(s.role, n)
        label = labelFor(s.role, n)
      }

      return [
        {
          id: s.id,
          title: s.title,
          level,
          children: below,
          ...(label ? { label } : {}),
          ...(s.role ? { role: s.role } : {}),
        },
      ]
    })

  const entries = walk(sections, 0)
  if (!notes) return entries
  return [
    ...entries,
    {
      id: notes.id,
      title: notes.title,
      label: labelFor('notes', 1),
      role: 'notes',
      level: 0,
      children: [],
    },
  ]
}

/** Re-level an entry and everything under it, after an ancestor was cut. */
function lift(entry: ContentsEntry, level: number): ContentsEntry {
  const shift = level - entry.level
  const move = (e: ContentsEntry): ContentsEntry => ({
    ...e,
    level: e.level + shift,
    children: e.children.map(move),
  })
  return move(entry)
}

/** Every entry in reading order, flattened — for scroll-spy and for counting. */
export function flattenContents(
  entries: readonly ContentsEntry[],
): ContentsEntry[] {
  return entries.flatMap((e) => [e, ...flattenContents(e.children)])
}
